import { useEffect, useMemo, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { FaSliders } from "react-icons/fa6";

const RATING_OPTIONS = [
  { label: "5", value: "5" },
  { label: "4", value: "4" },
];

const FEATURED_OPTIONS = ["Most Booked", "Top Rated"];

const toggleValue = (values, value) =>
  values.includes(value) ? values.filter((item) => item !== value) : [...values, value];

const getPlanPrice = (option) =>
  Number(option.pricePerSession || option.discountedPrice || option.basePrice || 0);

const formatPrice = (price) => `₹${Number(price || 0).toLocaleString("en-IN")}`;

const MarketplaceFilters = ({
  categories,
  selectedCategory,
  onSelectCategory,
  searchValue,
  onSearchChange,
  filters,
  filterOptions,
  experienceOptions,
  onFiltersChange,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [draftFilters, setDraftFilters] = useState(filters);
  const priceBounds = useMemo(() => {
    const prices = (filterOptions.pricing || [])
      .map(getPlanPrice)
      .filter((price) => Number.isFinite(price) && price > 0)
      .sort((a, b) => a - b);

    return {
      min: prices[0] || 0,
      max: prices[prices.length - 1] || 0,
    };
  }, [filterOptions.pricing]);

  useEffect(() => {
    setDraftFilters({
      ...filters,
      pricingRange: filters.pricingRange || (
        Number.isFinite(priceBounds.min) && Number.isFinite(priceBounds.max)
          ? { min: priceBounds.min, max: priceBounds.max }
          : null
      ),
    });
  }, [filters, priceBounds, showFilters]);

  const handleDraftToggle = (filterKey, value) => {
    setDraftFilters((currentFilters) => ({
      ...currentFilters,
      [filterKey]:
        filterKey === "expertise" && value === "all"
          ? []
          : toggleValue(currentFilters[filterKey] || [], value).filter((item) => item !== "all"),
    }));
  };

  const handleApplyFilters = () => {
    onFiltersChange(draftFilters);
    setShowFilters(false);
  };

  const handlePriceRangeChange = (field, value) => {
    const numericValue = Number(value);

    setDraftFilters((currentFilters) => {
      const currentRange = currentFilters.pricingRange || priceBounds;
      const nextRange = {
        min: currentRange.min,
        max: currentRange.max,
        [field]: numericValue,
      };

      if (nextRange.min > nextRange.max) {
        return {
          ...currentFilters,
          pricingRange: field === "min"
            ? { min: numericValue, max: numericValue }
            : { min: numericValue, max: numericValue },
        };
      }

      return {
        ...currentFilters,
        pricingRange: nextRange,
      };
    });
  };

  const renderCheckboxGroup = (title, filterKey, options, getValue = (option) => option) => {
    if (!options.length) {
      return null;
    }

    return (
      <div className="marketplace-filter-group">
        <p>{title}</p>
        <div className="marketplace-filter-options">
          {options.map((option) => {
            const value = getValue(option);
            const label = option.label || option.planName || option;

            return (
              <label key={value} className="marketplace-filter-option">
                <input
                  type="checkbox"
                  checked={
                    filterKey === "expertise" && value === "all"
                      ? (draftFilters.expertise || []).length === 0
                      : (draftFilters[filterKey] || []).includes(value)
                  }
                  onChange={() => handleDraftToggle(filterKey, value)}
                />
                <span>{label}</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  const renderPriceRange = () => {
    if (!Number.isFinite(priceBounds.min) || !Number.isFinite(priceBounds.max)) {
      return null;
    }

    const currentRange = draftFilters.pricingRange || priceBounds;
    const minPercent = ((currentRange.min - priceBounds.min) / (priceBounds.max - priceBounds.min || 1)) * 100;
    const maxPercent = ((currentRange.max - priceBounds.min) / (priceBounds.max - priceBounds.min || 1)) * 100;

    return (
      <div className="marketplace-filter-group marketplace-price-filter">
        <p>Price Range</p>
        <div
          className="marketplace-price-slider"
          style={{
            "--price-min-percent": `${minPercent}%`,
            "--price-max-percent": `${maxPercent}%`,
          }}
        >
          <input
            type="range"
            min={priceBounds.min}
            max={priceBounds.max}
            value={currentRange.min}
            onChange={(event) => handlePriceRangeChange("min", event.target.value)}
          />
          <input
            type="range"
            min={priceBounds.min}
            max={priceBounds.max}
            value={currentRange.max}
            onChange={(event) => handlePriceRangeChange("max", event.target.value)}
          />
        </div>
        <div className="marketplace-price-labels">
          <span>{formatPrice(currentRange.min)}</span>
          <span>{formatPrice(currentRange.max)}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="marketplace-controls">
      <div className="marketplace-category-row" aria-label="Marketplace categories">
        {categories.map((category) => (
          <button
            key={category}
            type="button"
            className={`marketplace-chip ${
              selectedCategory === category ? "marketplace-chip-active" : ""
            }`}
            onClick={() => onSelectCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>

      <div className="marketplace-search-row">
        <label className="marketplace-search" aria-label="Search practitioners">
          <FiSearch aria-hidden="true" />
          <input
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search by name, expertise..."
          />
        </label>
        <button
          type="button"
          className="marketplace-filter-button"
          aria-label="Filter practitioners"
          onClick={() => setShowFilters(true)}
        >
          <FaSliders aria-hidden="true" />
        </button>
      </div>

      {showFilters && (
        <div className="marketplace-filter-popover">
          <button
            type="button"
            className="marketplace-filter-close"
            aria-label="Close filters"
            onClick={() => setShowFilters(false)}
          >
            x
          </button>
          <div className="marketplace-filter-scroll">
            {renderCheckboxGroup(
              "Experience",
              "experience",
              experienceOptions.map((option) => ({ label: option.label, value: option.label })),
              (option) => option.value
            )}

            {renderCheckboxGroup("Rating", "rating", RATING_OPTIONS, (option) => option.value)}
            {renderCheckboxGroup("Featured", "featured", FEATURED_OPTIONS)}
            {renderCheckboxGroup(
              "Expertise",
              "expertise",
              [{ label: "All", value: "all" }, ...(filterOptions.expertise || [])],
              (option) => option.value
            )}
            {renderCheckboxGroup("Languages", "languages", filterOptions.languages || [])}
            {renderPriceRange()}
          </div>

          <button
            type="button"
            className="marketplace-apply-filter"
            onClick={handleApplyFilters}
          >
            Apply Filter
          </button>
        </div>
      )}
    </div>
  );
};

export default MarketplaceFilters;
